import { childEl } from '@qdabra/forms-shared/xmlHelpers';
import { nsPc } from '@qdabra/forms-shared/xmlNamespaces';
import { compose, isEmpty, map, prop, reject, when } from 'ramda';
import { parseXmlNative } from '../../utility/xmlHelpers';
import peopleSelector from '../UI/peopleSelector';
import {
    addPeopleToCustomPicker,
    CLASS_PEOPLE_PICKER_DROPDOWN_ITEM,
    updateFocusedItem,
} from '../helpers/peoplePicker.helpers';

const $ = window.jQuery;

const parseXML = parseXmlNative;

const personNodeXml =
    '<pc:Person xmlns:pc="http://schemas.microsoft.com/office/infopath/2007/PartnerControls">' +
    '<pc:DisplayName/>' +
    '<pc:AccountId/>' +
    '<pc:AccountType/>' +
    '</pc:Person>';

const createPersonNode = () => parseXML(personNodeXml).documentElement;

const makeEmptyPersonEntry = () => ({
    text: '',
    id: '',
    AccountId: '',
    DisplayName: '',
    EntityType: '',
});

// (DOMNode, PersonObject) -> void
function setPeoplePickerNodeValues(personNode, item) {
    const displayNameNode = childEl('DisplayName', nsPc)(personNode);
    const accountIdNode = childEl('AccountId', nsPc)(personNode);
    const accountTypeNode = childEl('AccountType', nsPc)(personNode);

    displayNameNode.textContent = item.DisplayName;
    accountIdNode.textContent = item.AccountId;
    accountTypeNode.textContent = item.EntityType;
}

// PersonObject -> DOMNode
const createPersonNodeFromObject = (item) => {
    let personNode = createPersonNode();

    setPeoplePickerNodeValues(personNode, item);

    return personNode;
};

// [PersonObject] -> [DOMNode]
const getPeopleNodes = compose(
    map(createPersonNodeFromObject),
    when(isEmpty, () => [makeEmptyPersonEntry()]),
);

async function setPeopleInPeoplePickerElement(personObjects, targetNode) {
    $(targetNode.getNode()).empty();

    const personNodes = getPeopleNodes(personObjects);

    await targetNode.appendChildrenAsync(personNodes);
}

const peoplePickerActionHandler = (
    qfsAccess,
    hostWebUrl,
    dataSources,
    renderView,
    peopleSearcher,
) => {
    const peopleSelectorInst = peopleSelector(
        qfsAccess,
        hostWebUrl,
        peopleSearcher,
    );

    const createPeoplePickerElementsInner = async (peopleObjects, props) => {
        const targetNode = peopleObjects
            ? dataSources.getNodeById(props.nodeId)
            : null;

        if (!targetNode) {
            return false;
        }

        await setPeopleInPeoplePickerElement(peopleObjects, targetNode);

        return true;
    };

    async function createPeoplePickerElement({ data, props }) {
        const shouldRender = await createPeoplePickerElementsInner(data, props);

        if (shouldRender) {
            await renderView();
        }
    }

    const handleCustomPickerResults = addPeopleToCustomPicker;

    const handlePeoplePickerClick = (isAddressBook) => async (eventInfo) => {
        const target = $(eventInfo.target).parent().find('.ppl-picker');

        const searchPeopleAsync = isAddressBook
            ? peopleSelectorInst.searchAddressBookAsync
            : peopleSelectorInst.checkUserNameAsync;

        const { canceled, values: users } = await searchPeopleAsync(eventInfo);

        if (canceled) {
            return;
        }

        // Removed users.length check. If the result is empty and it's a single people picker, we want to clear it.
        const allowMultiple = eventInfo.allowMultiple;

        handleCustomPickerResults(target, users, allowMultiple);
    };

    const filterSearchResults = (peopleList, selectedIds) =>
        reject((person) => selectedIds.includes(person.AccountId), peopleList);

    function createSearchDropdownItem(item) {
        const $item = $('<div>', {
            class: CLASS_PEOPLE_PICKER_DROPDOWN_ITEM,
            'data-id': item.AccountId,
            'data-EntityType': item.EntityType,
        });

        const $name = $('<div>', {
            class: 'picker-name',
            text: item.DisplayName,
        });

        const $email = $('<div>', {
            class: 'picker-email',
            text: item.Email || item.Description,
        });

        $item.append($name).append($email);

        return $item;
    }

    function displayNoResults($dropdown) {
        $dropdown.empty();

        $dropdown.append(
            '<div class="custom-picker-no-results">No results found</div>',
        );
    }

    let latestRequestTimestamp = 0;

    const populateDropdown = ($dropdown, results) => {
        $dropdown.empty();

        const dropdownItems = results.map(createSearchDropdownItem);
        $dropdown.append(dropdownItems);

        if (dropdownItems.length > 0) {
            updateFocusedItem(0, $dropdown);
        }
    };

    const displaySearchResults = (searchResult, props) => {
        const $dropdown = props.$dropdown;
        const selectedItems = props.$selectNode.data('selectedPpl');
        const selectedIds = selectedItems.map(prop('AccountId'));

        const availableResults = filterSearchResults(
            searchResult || [],
            selectedIds,
        );

        if (availableResults.length === 0) {
            displayNoResults($dropdown);
            return;
        }

        populateDropdown($dropdown, availableResults);

        $dropdown.show();
    };

    const displayLoadingIndicator = ($dropdown) => {
        $dropdown.empty();
        $dropdown.show();
        $dropdown.append('<div class="custom-picker-loading"></div>');
    };

    const searchUserForCustomPicker = async ({ props }) => {
        const requestTimestamp = Date.now();
        latestRequestTimestamp = requestTimestamp;

        displayLoadingIndicator(props.$dropdown);

        try {
            const searchResult = await peopleSearcher.searchPeople(
                props.searchText,
                props.searchPeopleOnly,
                props.maxCount,
            );

            // Ignore outdated requests
            if (requestTimestamp !== latestRequestTimestamp) {
                return;
            }

            displaySearchResults(searchResult, props);
        } catch (error) {
            console.error('Error querying people', error);

            if (requestTimestamp !== latestRequestTimestamp) {
                return;
            }

            const errorMessage = error.message || 'Error loading results';

            props.$dropdown.empty();

            $('<div>')
                .addClass('custom-picker-no-results')
                .text(`Error loading results: ${errorMessage}`)
                .appendTo(props.$dropdown);
        }
    };

    return {
        createPeoplePickerElement,
        handlePeopleAddressSearch: handlePeoplePickerClick(true),
        handlePeopleUserCheck: handlePeoplePickerClick(false),
        searchUserForCustomPicker,
    };
};

export default peoplePickerActionHandler;
